/**
 * @fileoverview The ONE wait-and-re-send step for a same-key request the server is still running
 * (SWALLOWED-CATCH-BURNDOWN-001 finish-gate unit M, coordinator ruling a-client).
 *
 * The shared server guard (`@puffin-core/middleware` `createIdempotencyKeyMiddleware`) claims an
 * `Idempotency-Key` atomically before its handler runs. A second request with the same key that arrives
 * while the first is still inside its handler is answered `409 IDEMPOTENCY_IN_PROGRESS` with a
 * `Retry-After` in whole seconds — never a second run of the handler. That answer is not a failure:
 * once the holder finishes, the same request replays its recorded response. So this step waits
 * `Retry-After` and re-sends the SAME request config (the same key, the same body), and the caller
 * sees a failure only when the bound is spent.
 *
 * ## The two bounds, and where each comes from
 *
 * - **One wait:** the server's own `Retry-After`, never above {@link MAX_IN_PROGRESS_WAIT_SECONDS} —
 *   the value the server sends (`RETRY_AFTER_SECONDS = 5`, `createLookupFailurePosture`). A missing,
 *   zero or non-numeric header (an HTTP-date) waits that same value.
 * - **How many waits:** as many as fit the request's own timeout (`config.timeout`; the shared client
 *   defaults it to {@link DEFAULT_REQUEST_TIMEOUT_MS}). The claim the client is waiting on belongs to a
 *   run this client already gave the request's own time budget, so it waits no longer than that budget
 *   again. A request with no timeout (`0`) is budgeted as the shared client's default.
 *
 * It is a step for ANY axios instance, the `retryAfterStepUp` precedent: `createApiClient` takes it in
 * its response interceptor, and a bespoke transport (pos-web's `ApiClientManager`) takes the SAME step.
 *
 * @module api-client/idempotencyInProgressRetry
 */

import { retryAfterMsOf } from '@puffin-core/retry-after';

/** The refusal code the server guard answers while the key's first request is still running. */
export const IDEMPOTENCY_IN_PROGRESS_CODE = 'IDEMPOTENCY_IN_PROGRESS';

/** The longest one wait, in seconds: the server's own `Retry-After` (`RETRY_AFTER_SECONDS`). */
export const MAX_IN_PROGRESS_WAIT_SECONDS = 5;

/** The shared client's request timeout when a caller names none (`createApiClient`'s `timeout`). */
export const DEFAULT_REQUEST_TIMEOUT_MS = 30000;

/** The `HTTP 409` status. */
const CONFLICT_STATUS = 409;

/**
 * @param {*} error - an axios rejection
 * @returns {boolean} whether it is the server's in-progress refusal
 */
function isInProgressRefusal(error) {
  return (
    error?.response?.status === CONFLICT_STATUS &&
    error.response.data?.error?.code === IDEMPOTENCY_IN_PROGRESS_CODE
  );
}

/**
 * The wait the server asked for, in milliseconds, capped at {@link MAX_IN_PROGRESS_WAIT_SECONDS}.
 *
 * @param {*} headers - the 409's response headers
 * @returns {number}
 */
function waitMsOf(headers) {
  const maxMs = MAX_IN_PROGRESS_WAIT_SECONDS * 1000;
  // The ONE reader (`@puffin-core/retry-after`): delay-seconds or an HTTP-date; no usable wait is the full wait.
  const asked = retryAfterMsOf(headers);
  return (asked ?? 0) > 0 ? Math.min(asked, maxMs) : maxMs;
}

/**
 * How many re-sends fit the request's own timeout.
 *
 * @param {Object} config - the request config (axios has merged the instance's timeout into it)
 * @returns {number}
 */
function resendBudgetOf(config) {
  const timeoutMs =
    Number.isFinite(config.timeout) && config.timeout > 0
      ? config.timeout
      : DEFAULT_REQUEST_TIMEOUT_MS;
  return Math.floor(timeoutMs / (MAX_IN_PROGRESS_WAIT_SECONDS * 1000));
}

/** @returns {DOMException} the standard abort shape every transport converges on */
function abortError() {
  return new DOMException('The operation was aborted.', 'AbortError');
}

/**
 * Wait `ms`, or reject `AbortError` the moment `signal` aborts.
 *
 * @param {number} ms
 * @param {?AbortSignal} signal
 * @returns {Promise<void>}
 */
function waitUnlessAborted(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * The step: on `409 IDEMPOTENCY_IN_PROGRESS`, wait the server's `Retry-After` and re-send the SAME
 * config — while the request's budget lasts.
 *
 * @param {Object} params
 * @param {Function} params.client - the axios instance (callable with a request config)
 * @param {*} params.error - the rejection being handled
 * @returns {?Promise<*>} the re-sent request's settlement, or null when this is no in-progress refusal
 *   to wait on (or the budget is spent — the caller's ordinary refusal path then runs)
 */
export function retryWhileIdempotencyInProgress({ client, error }) {
  const config = error?.config;
  if (!config || !isInProgressRefusal(error)) return null;
  const resent = config._idempotencyInProgressResends ?? 0;
  if (resent >= resendBudgetOf(config)) return null;
  config._idempotencyInProgressResends = resent + 1;
  return waitUnlessAborted(waitMsOf(error.response.headers), config.signal).then(() =>
    client(config),
  );
}
